import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { axiosPrivate } from "../api";
import { formatToUrl } from "../utils";

const api = axiosPrivate();
const port = process.env.REACT_APP_BACKEND_PORT;

/** Şube sunucusu ilk hesaplamada yavaş kalınca arka uç 504 döndürüyor; bu kadar kez yeniden denenir. */
const MAX_ATTEMPTS = 3;
/** Şube özeti bu süre içinde yeniden istenmez (ms). */
const SUMMARY_FRESH_MS = 30 * 1000;

const initialState = {
  allReports: {
    reports: [],
    loading: false,
    error: null,
    success: false,
    branchUrl: null,
  },
  reportDetails: {
    data: null,
    loading: false,
    error: null,
    success: false,
    attempt: 0, // 0 = deneme yok, 2+ = yeniden deneniyor
    stale: false, // önbellekten gösteriliyor, tazesi yolda
  },
  // Başarılı rapor sonuçları: anahtar = şube|rapor|başlangıç|bitiş
  reportCache: {},
  reportSummary: null,
};

const cacheKeyOf = ({ branchUrl, reportId, start_date, end_date }) =>
  `${branchUrl}|${reportId}|${start_date || ""}|${end_date || ""}`;

const isRetriable = (err) => {
  const status = err?.response?.status;
  return (
    status === 502 ||
    status === 503 ||
    status === 504 ||
    err?.code === "ECONNABORTED" ||
    err?.code === "ERR_NETWORK"
  );
};

const reportSlice = createSlice({
  name: "report",
  initialState: initialState,
  reducers: {
    resetAllReportsState: (state) => {
      state.allReports = initialState.allReports;
    },
    resetReportDetailsState: (state) => {
      state.reportDetails = initialState.reportDetails;
    },
    resetGetSummaryState: (state) => {
      state.reportSummary = initialState.reportSummary;
    },
    reportDetailsRetrying: (state, action) => {
      state.reportDetails.attempt = action.payload.attempt;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllReports.pending, (state, action) => {
        state.allReports = {
          loading: true,
          success: false,
          reports: [],
          error: null,
          branchUrl: action.meta.arg.branchUrl,
        };
      })
      .addCase(getAllReports.fulfilled, (state, action) => {
        state.allReports = {
          loading: false,
          reports: action.payload.reports,
          success: true,
          error: null,
          branchUrl: action.payload.branchUrl,
        };
      })
      .addCase(getAllReports.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.allReports = {
          loading: false,
          success: false,
          reports: [],
          error: action.error.message,
          branchUrl: action.meta.arg.branchUrl,
        };
      })
      .addCase(getReportDetails.pending, (state, action) => {
        const cached = state.reportCache[cacheKeyOf(action.meta.arg)];
        state.reportDetails = {
          loading: true,
          success: Boolean(cached),
          data: cached ? cached.data : null,
          error: null,
          attempt: 1,
          stale: Boolean(cached),
        };
      })
      .addCase(getReportDetails.fulfilled, (state, action) => {
        state.reportDetails = {
          loading: false,
          data: action.payload.data,
          success: true,
          error: null,
          attempt: 0,
          stale: false,
        };
        state.reportCache[action.payload.cacheKey] = {
          data: action.payload.data,
          fetchedAt: Date.now(),
        };
      })
      .addCase(getReportDetails.rejected, (state, action) => {
        // Kullanıcı başka rapora geçti; iptal edilen isteğin sonucu yok sayılır.
        if (action.meta.aborted) return;
        const hadData = Boolean(state.reportDetails.data);
        state.reportDetails = {
          ...state.reportDetails,
          loading: false,
          success: hadData,
          error: action.error.message,
          attempt: 0,
          stale: hadData,
        };
      })
      .addCase(getReportSummary.pending, (state, action) => {
        state.reportSummary = {
          ...state.reportSummary,
          [action.meta.arg.branchUrl]: {
            ...(state.reportSummary?.[action.meta.arg.branchUrl] || {}),
            loading: true,
            success: false,
            error: null,
          },
        };
      })
      .addCase(getReportSummary.fulfilled, (state, action) => {
        state.reportSummary = {
          ...state.reportSummary,
          [action.payload.branchUrl]: {
            loading: false,
            success: true,
            error: null,
            data: action.payload.data,
            fetchedAt: Date.now(),
          },
        };
      })
      .addCase(getReportSummary.rejected, (state, action) => {
        if (action.meta.condition) return; // taze olduğu için atlandı
        state.reportSummary = {
          ...state.reportSummary,
          [action.meta.arg.branchUrl]: {
            loading: false,
            success: false,
            error: action.error,
            fetchedAt: Date.now(),
          },
        };
      });
  },
});

export const getAllReports = createAsyncThunk(
  "reports/getAllReports",
  async ({ branchUrl }) => {
    const formattedUrl = formatToUrl(branchUrl);
    const response = await api.get(`${port}/reports/get_reports`, {
      params: { path: formattedUrl },
    });
    return { branchUrl, reports: response.data };
  },
  {
    // Aynı şubenin listesi zaten eldeyse yeniden çekilmez (force ile zorlanır).
    condition: ({ branchUrl, force }, { getState }) => {
      const current = getState().reports.allReports;
      if (force) return true;
      return !(current.success && current.branchUrl === branchUrl);
    },
  }
);

export const getReportDetails = createAsyncThunk(
  "reports/getReportDetails",
  async (
    { branchUrl, reportId, start_date, end_date },
    { signal, dispatch }
  ) => {
    const formattedUrl = formatToUrl(branchUrl);
    const params = {
      ...(start_date ? { start_date } : {}),
      ...(end_date ? { end_date } : {}),
    };
    const cacheKey = cacheKeyOf({ branchUrl, reportId, start_date, end_date });

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const response = await api.get(`${port}/reports/get_report_table`, {
          params: {
            params: {
              report: reportId,
              use_work_period: false,
              ...params,
            },
            path: formattedUrl,
          },
          signal,
        });
        return { data: response.data, cacheKey };
      } catch (err) {
        if (signal.aborted || axios.isCancel(err)) throw err;
        if (!isRetriable(err) || attempt === MAX_ATTEMPTS) throw err;
        dispatch(reportDetailsRetrying({ attempt: attempt + 1 }));
        await new Promise((resolve) => setTimeout(resolve, 800 * attempt));
        if (signal.aborted) throw new Error("aborted");
      }
    }
    throw new Error("unreachable");
  }
);

export const getReportSummary = createAsyncThunk(
  "reports/getSummary",
  async ({ branchUrl }, { rejectWithValue }) => {
    try {
      const formattedUrl = formatToUrl(branchUrl);
      const response = await api.get(`${port}/reports/report_summary`, {
        params: { path: formattedUrl },
      });
      const { data } = response;
      if (!data || !data.tables) {
        return rejectWithValue("No rows found in the report data.");
      }
      return { branchUrl, data: response.data };
    } catch (err) {
      return rejectWithValue(
        err.message || "An error occurred while fetching the report summary."
      );
    }
  },
  {
    // Kısa süre önce alınmış özet yeniden istenmez; yenile düğmesi force ile zorlar.
    condition: ({ branchUrl, force }, { getState }) => {
      if (force) return true;
      const entry = getState().reports.reportSummary?.[branchUrl];
      if (!entry) return true;
      if (entry.loading) return false;
      return !(entry.fetchedAt && Date.now() - entry.fetchedAt < SUMMARY_FRESH_MS);
    },
  }
);

export default reportSlice.reducer;
export const {
  resetAllReportsState,
  resetGetSummaryState,
  resetReportDetailsState,
  reportDetailsRetrying,
} = reportSlice.actions;
export const MAX_REPORT_ATTEMPTS = MAX_ATTEMPTS;
